import { Collapse } from 'antd';
import { useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';

import { Button } from 'common/components';
import { I18nNameSpaces } from 'common/services/i18n';
import { AppState } from 'common/models/AppState';
import { PdfRenderer } from 'common/pdf/pdf-renderer';
import { SignatureScope } from 'common/models/Transaction';

import { PageControls } from '../../../../components';
import {
  selectInformationsCustomerInfo1,
  selectInformationsCustomerInfo2,
  selectIsCustomerInfo2Visible,
  selectInformationsCompanyInfo,
  selectInvestingAgreements,
  selectInvestingAnalyse,
  selectInvestingExperience,
  selectInvestingHazard,
  selectStrategy,
  selectInvestingFinances,
} from '../../../../ducks';

export default function InvestingPDFFeature() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation(I18nNameSpaces.Forms);
  const appState = useStore<AppState>().getState();
  const pdfRenderer = new PdfRenderer(appState);

  const companyInfo = useSelector(selectInformationsCompanyInfo);
  const clientValues1 = useSelector(selectInformationsCustomerInfo1);
  const clientValues2 = useSelector(selectInformationsCustomerInfo2);
  const isCustomerInfo2Visible = useSelector(selectIsCustomerInfo2Visible);

  const analyse = useSelector(selectInvestingAnalyse);
  const agreement = useSelector(selectInvestingAgreements);
  const hazard = useSelector(selectInvestingHazard);
  const investingExperience = useSelector(selectInvestingExperience);
  const strategy = useSelector(selectStrategy);
  const finances = useSelector(selectInvestingFinances);

  function handleBackClick() {
    navigate(`/form/${id}/investing/agreements`);
  }

  function handleContinueClick() {
    navigate(`/form/${id}/protocol`);
  }

  const collapseItems = [
    {
      key: 'client1',
      label: t('signatureForm.fileTypes.customerInfo1'),
      children:
        clientValues1 && companyInfo && agreement ? (
          <PDFDownloadLink
            document={pdfRenderer.renderDocument(SignatureScope.CUSTOMER_INFO_1)}
            fileName={t('signatureForm.fileNames.customerInfo1')}
          >
            {({ loading }: { loading: boolean }) => (
              <Button block disabled={loading} loading={loading}>
                {t('signatureForm.fileTypes.customerInfo1')}
              </Button>
            )}
          </PDFDownloadLink>
        ) : (
          t('signatureForm.fileTypes.valuesMissing')
        ),
    },
    ...(isCustomerInfo2Visible
      ? [
          {
            key: 'client2',
            label: t('signatureForm.fileTypes.customerInfo2'),
            children:
              clientValues2 && companyInfo && agreement ? (
                <PDFDownloadLink
                  document={pdfRenderer.renderDocument(SignatureScope.CUSTOMER_INFO_2)}
                  fileName={t('signatureForm.fileNames.customerInfo2')}
                >
                  {({ loading }: { loading: boolean }) => (
                    <Button block disabled={loading} loading={loading}>
                      {t('signatureForm.fileTypes.customerInfo2')}
                    </Button>
                  )}
                </PDFDownloadLink>
              ) : (
                t('signatureForm.fileTypes.valuesMissing')
              ),
          },
        ]
      : []),
    {
      key: 'client3',
      label: t('signatureForm.fileTypes.investing'),
      children:
        analyse &&
        investingExperience &&
        clientValues1 &&
        companyInfo &&
        agreement &&
        hazard &&
        investingExperience &&
        strategy &&
        finances ? (
          <PDFDownloadLink
            document={pdfRenderer.renderDocument(SignatureScope.INVESTING)}
            fileName={t('signatureForm.fileNames.investing')}
          >
            {({ loading }: { loading: boolean }) => (
              <Button block disabled={loading} loading={loading}>
                {t('signatureForm.fileTypes.investing')}
              </Button>
            )}
          </PDFDownloadLink>
        ) : (
          t('signatureForm.fileTypes.valuesMissing')
        ),
    },
  ];

  return (
    <>
      <div className="transaction-feature" style={{ margin: '30px 0' }}>
        <Collapse accordion destroyOnHidden items={collapseItems} />
      </div>
      <PageControls onBack={handleBackClick} onContinue={handleContinueClick} />
    </>
  );
}
